import React, { useState, useContext, useRef, useCallback, useEffect, ElementType, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  FormControl,
  FormControlLabel,
  Badge,
  Button,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  FormGroup,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid as GridMUI,
} from '@mui/material';
import {
  Grid,
  TableColumnVisibility,
  Table,
  TableHeaderRow,
  PagingPanel,
  TableFilterRow,
  TableColumnResizing,
  SearchPanel,
  Toolbar,
  ExportPanel,
} from '@devexpress/dx-react-grid-material-ui';
import {
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  FilteringState,
  IntegratedFiltering,
  SearchState,
  Filter,
  Sorting,
  DataTypeProvider,
} from '@devexpress/dx-react-grid';
import saveAs from 'file-saver';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DateRange, ExpandMore } from '@mui/icons-material';
import DataContext from '../../../context/DataProvider/DataContext';
import columns from './columns';
import shopifyColumns from './ShopifyColumns';
import { AgentStatus } from '../../../types/Agent.types';
import columnWidths from './columnsWidths';
import RowsProvider from './RowsProvider';
import { filterRowMessages } from '../../../components/Table/Table';

// @ts-expect-error
const onSaveFilterXlsx = (workbook) => {
  // @ts-expect-error
  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'FilterDataGrid.xlsx');
  });
};
// @ts-expect-error
const onSaveXlsx = (workbook) => {
  // @ts-expect-error
  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};
// @ts-expect-error
const onSaveCSV = (workbook) => {
  // @ts-expect-error
  workbook.csv.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'shopify.csv');
  });
};

interface PropeFilter {
  type: any;
  restProps: any;
  component: ElementType<any>;
}

function FilterIcon({ type, ...restProps }: PropeFilter) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  if (type === 'month') return <DateRange {...restProps} />;
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <TableFilterRow.Icon type={type} {...restProps} />;
}

// LocalStorage Helper-Funktionen
const COLUMN_VISIBILITY_KEY = 'allAgentsTable_columnVisibility';

const saveColumnVisibilityToStorage = (checkedColumns: string[]) => {
  try {
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(checkedColumns));
  } catch (error) {
    console.error('Fehler beim Speichern der Spaltenauswahl:', error);
  }
};

const loadColumnVisibilityFromStorage = (): string[] | null => {
  try {
    const stored = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Fehler beim Laden der Spaltenauswahl:', error);
  }
  return null;
};

const defaultHiddenColumnsName = [
  'dismissal_reason',
  'dismissed_at',
  'business_registration_date',
  'branch_office',
  'business_owner',
  'contract_type',
  'is_small_business',
  'address2',
  'fax',
  'status',
  'rank',
];

const defaultVisibleColumns = columns.filter((col) => !defaultHiddenColumnsName.includes(col.name));
const defaultCheckedColumnNames = defaultVisibleColumns.map((col) => col.name);

function AllAgentsTable() {
  const { brachOffices, agents, status, loaderFun } = useContext(DataContext);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizes] = useState([10, 25, 50, 100]);
  const [sorting, setSorting] = useState<Sorting[]>([{ columnName: 'branch_office', direction: 'asc' }]);
  const [defaultColumnWidths] = useState(columnWidths);
  const [defaultHiddenColumnNames] = useState(['id']);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [, setIsDismissedAgentFilter] = useState(false);
  const [dateColumns] = useState(['birthday', 'start_date', 'dismissed_at', 'business_registration_date']);
  const [dateFilterOperations] = useState(['month', 'contains', 'startsWith', 'endsWith']);
  const [agentsStatus, setAgentStatus] = useState(3);
  const [agentsOffOffice, setAgentsOffOffice] = useState(1);
  const [sortMode, setSortMode] = useState<'standard' | 'startMonth'>('standard');
  const [searchValue, setSearchValue] = useState('');
  const [filteringColumnExtensions] = useState([
    {
      columnName: 'birthday',
      predicate: (value: any, filter: any, row: any) => {
        if (!filter.value.length) return true;
        if (filter && filter.operation === 'month') {
          const month = parseInt(value.split('-')[1], 10);
          return month === parseInt(filter.value, 10);
        }
        return IntegratedFiltering.defaultPredicate(value, filter, row);
      },
    },
    {
      columnName: 'start_date',
      predicate: (value: any, filter: any, row: any) => {
        if (!filter.value.length) return true;
        if (filter && filter.operation === 'month') {
          if (value === null) {
            return false;
          }
          const month = parseInt(value.split('-')[1], 10);
          return month === parseInt(filter.value, 10);
        }
        return IntegratedFiltering.defaultPredicate(value, filter, row);
      },
    },
    {
      columnName: 'dismissed_at',
      predicate: (value: any, filter: any, row: any) => {
        if (!filter.value.length) return true;
        if (filter && filter.operation === 'month') {
          if (value === null) {
            return false;
          }
          const month = parseInt(value.split('-')[1], 10);
          return month === parseInt(filter.value, 10);
        }
        return IntegratedFiltering.defaultPredicate(value, filter, row);
      },
    },
    {
      columnName: 'business_registration_date',
      predicate: (value: any, filter: any, row: any) => {
        if (!filter.value.length) return true;
        if (filter && filter.operation === 'month') {
          if (value === null) {
            return false;
          }
          const month = parseInt(value.split('-')[1], 10);
          return month === parseInt(filter.value, 10);
        }
        return IntegratedFiltering.defaultPredicate(value, filter, row);
      },
    },
  ]);
  const [exportedRows, setExportedRows] = useState([]);
  const [hiddenColumnNames] = useState([]);
  const [columnsM, setColumns] = useState(columns);
  const [checkedColumns, setCheckedColumns] = useState(columns.map((col) => col.name));

  const refExportFilter = useRef<typeof GridExporter>(null);
  const refExportApp = useRef<typeof GridExporter>(null);
  const refExportShopify = useRef<typeof GridExporter>(null);

  useEffect(() => {
    if (!status) {
      loaderFun.loadStatus();
    }
  }, []);

  const startFilterExport = useCallback(() => {
    refExportFilter.current?.exportGrid({ selectedOnly: true });
  }, [refExportFilter]);

  const startExport = useCallback(() => {
    refExportApp.current?.exportGrid();
  }, [refExportApp]);

  const startShopifyExport = useCallback(() => {
    refExportShopify.current?.exportGrid();
  }, [refExportShopify]);

  const TableRow = useCallback(
    ({ row, children, tableRow }: Table.DataRowProps) => (
      <Table.Row
        key={Math.random()}
        tableRow={tableRow}
        row={row}
        // eslint-disable-next-line react/no-children-prop
        children={children}
        onClick={() => {
          navigate(`/agent/${row.agent_id}`);
        }}
        style={{ cursor: 'pointer' }}
      />
    ),
    [],
  );

  const changeCurrentPage = useCallback((value) => {
    setCurrentPage(value);
  }, []);

  const changePageSize = useCallback((value) => {
    setPageSize(value);
  }, []);

  const allFilterReset = useCallback(() => {
    setFilters([]);
    setIsDismissedAgentFilter(false);
    setAgentStatus(3);
    setAgentsOffOffice(1);
    setSortMode('standard');
    setSearchValue('');
    setCurrentPage(0);
    // Spaltenauswahl auf Default zurücksetzen
    setColumns(defaultVisibleColumns);
    setCheckedColumns(defaultCheckedColumnNames);
    saveColumnVisibilityToStorage(defaultCheckedColumnNames);
  }, []);

  const ToolbarRootBase = useCallback(
    ({ children }) => (
      <Toolbar.Root>
        <Stack direction="row" width="100%" gap={2}>
          {children}
        </Stack>
      </Toolbar.Root>
    ),
    [],
  );

  const exportMenu = useCallback(
    () => (
      <Box display="flex" flexDirection="column">
        <Button onClick={startExport}>Alle</Button>
        <Button onClick={startFilterExport}>Filter</Button>
        <Button onClick={startShopifyExport}>Shopify</Button>
      </Box>
    ),
    [],
  );

  // Memoize visibleColumns to prevent unnecessary re-renders
  const visibleColumns = useMemo(
    () =>
      /* @ts-ignore */
      columns.filter((column) => !hiddenColumnNames.includes(column.name)),
    [columns, hiddenColumnNames],
  );

  const activeAgents = useMemo(() => agents.filter((agent) => agent.status.id === AgentStatus.active), [agents]);

  const dismissedAgents = useMemo(() => agents.filter((agent) => agent.status.id === AgentStatus.dismissed), [agents]);

  const imProcessAgents = useMemo(() => agents.filter((agent) => agent.status.id === AgentStatus.inProcess), [agents]);

  const agentsFailed = useMemo(() => agents.filter((agent) => agent.status.id === AgentStatus.failed), [agents]);

  function agentsOnStatus(statusID: number) {
    switch (statusID) {
      case AgentStatus.active:
        return activeAgents;
      case AgentStatus.dismissed:
        return dismissedAgents;
      case AgentStatus.inProcess:
        return imProcessAgents;
      case AgentStatus.failed:
        return agentsFailed;
      default:
        return activeAgents;
    }
  }

  const agentsOfOffice = useMemo(
    () => agentsOnStatus(agentsStatus).filter((agent) => agent.branch_office.id === agentsOffOffice),
    [agentsOffOffice, agentsStatus, agents, visibleColumns],
  );

  // Sortierte Version der Daten basierend auf sortMode
  const sortedAgents = useMemo(() => {
    if (sortMode === 'startMonth') {
      const agentsCopy = [...agentsOfOffice];
      return agentsCopy.sort((a, b) => {
        // Sortiere nach Monat (numerisch)
        const monthA = a.start_date ? parseInt(a.start_date.split('-')[1], 10) : 13;
        const monthB = b.start_date ? parseInt(b.start_date.split('-')[1], 10) : 13;
        if (monthA !== monthB) {
          return monthA - monthB;
        }
        // Bei gleichem Monat, sortiere nach Jahr (ältestes zuerst)
        const yearA = a.start_date ? parseInt(a.start_date.split('-')[0], 10) : 9999;
        const yearB = b.start_date ? parseInt(b.start_date.split('-')[0], 10) : 9999;
        return yearA - yearB;
      });
    }
    // Standard-Sortierung: unveränderte Daten
    return agentsOfOffice;
  }, [agentsOfOffice, sortMode]);

  // Initialisierung der Spalten - nur einmal beim ersten Mount
  useEffect(() => {
    // Versuche gespeicherte Einstellungen zu laden
    const savedColumnVisibility = loadColumnVisibilityFromStorage();

    if (savedColumnVisibility && savedColumnVisibility.length > 0) {
      // Verwende gespeicherte Einstellungen
      const storedVisibleColumns = columns.filter((col) => savedColumnVisibility.includes(col.name));
      setColumns(storedVisibleColumns);
      setCheckedColumns(savedColumnVisibility);
    } else {
      // Verwende Default-Einstellungen nur wenn keine gespeicherten vorhanden sind
      setColumns(defaultVisibleColumns);
      setCheckedColumns(defaultCheckedColumnNames);
      saveColumnVisibilityToStorage(defaultCheckedColumnNames);
    }
  }, []);

  const handleCheckboxChange = useMemo(
    () => (columnName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        // Füge die Spalte an ihrer ursprünglichen Position hinzu
        const columnToAdd = columns.find((col) => col.name === columnName);
        if (columnToAdd) {
          setColumns((prevColumns) => {
            const updatedColumns = [...prevColumns, columnToAdd];
            return updatedColumns.sort(
              (a, b) =>
                columns.findIndex((col) => col.name === a.name) - columns.findIndex((col) => col.name === b.name),
            );
          });
          setCheckedColumns((prev) => {
            const updatedCheckedColumns = [...prev, columnName];
            // Speichere die neue Auswahl im localStorage
            saveColumnVisibilityToStorage(updatedCheckedColumns);
            return updatedCheckedColumns;
          });
        }
      } else {
        // Entferne die Spalte, wenn Checkbox deaktiviert ist
        setColumns((prevColumns) => prevColumns.filter((col) => col.name !== columnName));
        setCheckedColumns((prev) => {
          const updatedCheckedColumns = prev.filter((name) => name !== columnName);
          // Speichere die neue Auswahl im localStorage
          saveColumnVisibilityToStorage(updatedCheckedColumns);
          return updatedCheckedColumns;
        });
      }
    },
    [columns, checkedColumns],
  );

  return (
    <>
      <Box>
        <Accordion
          sx={{
            border: '1px solid rgba(255, 255, 255, 0.23)',
            borderRadius: '4px',
            padding: '8px',
            boxShadow: 'none',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1-content" id="panel1-header">
            <GridMUI container alignItems="center" justifyContent="space-between">
              <GridMUI item>
                <Stack direction="row" spacing={2}>
                  <Badge badgeContent={checkedColumns.length} color="secondary">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>Sichtbar</Typography>
                      <VisibilityIcon />
                    </Stack>
                  </Badge>
                  <Badge badgeContent={columns.length - checkedColumns.length} color="primary">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>Unsichtbar</Typography>
                      <VisibilityOffIcon />
                    </Stack>
                  </Badge>
                </Stack>
              </GridMUI>
              <GridMUI item>
                <Typography>Spaltenauswahl</Typography>
              </GridMUI>
            </GridMUI>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: '10px',
              }}
            >
              {columns.map((column) => (
                <FormControlLabel
                  key={column.name}
                  control={
                    <Checkbox
                      checked={checkedColumns.includes(column.name)}
                      onChange={handleCheckboxChange(column.name)}
                      name={column.name}
                    />
                  }
                  label={column.title}
                />
              ))}
            </FormGroup>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button variant="contained" color="success" disableRipple onClick={allFilterReset}>
                FILTER ZURÜCKSETZEN
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Stack paddingBottom={2} direction="row" alignItems="center" justifyContent="space-evenly" gap={5} paddingTop={2}>
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Firma</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={agentsOffOffice}
            label="Firma"
            onChange={(event) => {
              setAgentsOffOffice(event.target.value as number);
            }}
          >
            {brachOffices.map((office) => (
              <MenuItem key={office.id} value={office.id}>
                {office.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Berater Status</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={agentsStatus}
            label="Berater Status"
            onChange={(event) => {
              setAgentStatus(event.target.value as number);
            }}
          >
            {status
              .filter((statis) => statis.id !== 8 && statis.id !== 6 && statis.id !== 4)
              .map((stati) => (
                <MenuItem value={stati.id}>{stati.title}</MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="sort-mode-select-label">Sortierung</InputLabel>
          <Select
            labelId="sort-mode-select-label"
            id="sort-mode-select"
            value={sortMode}
            label="Sortierung"
            onChange={(event) => {
              setSortMode(event.target.value as 'standard' | 'startMonth');
            }}
          >
            <MenuItem value="standard">Standard-Sortierung</MenuItem>
            <MenuItem value="startMonth">Nach Start-Monat/Jahr sortieren</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Paper>
        <Grid rows={sortedAgents} columns={columnsM}>
          {/* <NumberTypeProvider /> */}
          <DataTypeProvider for={dateColumns} availableFilterOperations={dateFilterOperations} />
          <SearchState value={searchValue} onValueChange={setSearchValue} />
          <FilteringState filters={filters} onFiltersChange={setFilters} />
          <IntegratedFiltering columnExtensions={filteringColumnExtensions} />
          <RowsProvider onRowsUpdate={setExportedRows} />
          <SortingState sorting={sorting} onSortingChange={setSorting} />
          <PagingState
            currentPage={currentPage}
            onCurrentPageChange={changeCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
          />
          <IntegratedPaging />
          <IntegratedSorting />
          <Table rowComponent={TableRow} messages={{ noData: 'Keine Daten' }} />
          <TableColumnResizing columnWidths={defaultColumnWidths} />
          <TableHeaderRow showSortingControls messages={{ sortingHint: 'Sortieren' }} />
          <TableColumnVisibility defaultHiddenColumnNames={defaultHiddenColumnNames} />
          <TableFilterRow
            showFilterSelector
            // @ts-ignore - FilterIcon hat erweiterte Props
            iconComponent={FilterIcon}
            messages={{ ...filterRowMessages, month: 'Monat gleich' }}
          />
          <Toolbar rootComponent={ToolbarRootBase} />
          <SearchPanel messages={{ searchPlaceholder: 'Suchen...' }} />

          <PagingPanel
            pageSizes={pageSizes}
            messages={{
              showAll: 'Alle',
              rowsPerPage: 'Zeilen pro Seite',
              info: ({ from, to, count }) => `${from}–${to} von ${count}`,
            }}
          />
          <ExportPanel menuItemComponent={exportMenu} />
        </Grid>

        <GridExporter ref={refExportFilter} rows={exportedRows} columns={columnsM} onSave={onSaveFilterXlsx} />
        <GridExporter ref={refExportApp} rows={sortedAgents} columns={visibleColumns} onSave={onSaveXlsx} />
        <GridExporter ref={refExportShopify} rows={sortedAgents} columns={shopifyColumns} onSave={onSaveCSV} />
      </Paper>
    </>
  );
}

export default AllAgentsTable;
